import { createContext } from 'react';
import type { ContextValue } from 'react-aria-components';
import {
  SelectValue as ReactAriaSelectValue,
  SelectValueProps as ReactAriaSelectValueProps,
  useContextProps,
} from 'react-aria-components';
import { mergeClassNames } from '../../../../utils/mergeClassNames';
import { SelectBaseProps } from './SelectBase';
import styles from './SelectValue.module.scss';

export type SelectBaseValueProps = ReactAriaSelectValueProps<object> & {
  /**
   * The size, impacting the value's typography.
   * @default 'medium'
   */
  size?: SelectBaseProps['size'];
};

export type SelectBaseValueContextValue = ContextValue<
  Omit<SelectBaseValueProps, 'children'>,
  HTMLDivElement
>;

export const SelectBaseValueContext =
  createContext<SelectBaseValueContextValue>({});

/**
 * A styled SelectValue from `react-aria-components`. Useful when creating a custom Select.
 *
 * Merges context props from `SelectBaseValueContext` with local props (preferring local props).
 */
export const SelectBaseValue = (props: SelectBaseValueProps) => {
  const [contextProps] = useContextProps(props, null, SelectBaseValueContext);
  const { className, size = 'medium', ...otherProps } = contextProps;

  return (
    <ReactAriaSelectValue
      {...otherProps}
      className={(renderProps) =>
        mergeClassNames([styles.selectValue, className], renderProps)
      }
      data-size={size}
    />
  );
};
